import React, { useCallback, useMemo, useState } from "react";

import { faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { faBook } from "@fortawesome/pro-light-svg-icons";
import { useSelector } from "react-redux";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { BulkUpdateBusinessModal } from "components/BulkUpdateOptionalFieldsModal/Businesses";
import { Column } from "components/Layout";

import { AgGridTables } from "constants/aggrid";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";

import {
  openCreateGlobalBusinessModal,
  openEditBusinessModal,
} from "lib/navigation";

import {
  getDefaultBillingSettings,
  getIsLivestockAgent,
  getProperties,
  selectDefaultVendorSplitByBusinessIdLookup,
} from "selectors";

import { selectBusinessesAggridData } from "selectors/aggrid/businesses";

import {
  useBoolean,
  useDebounceSelector,
  useHasDeploymentPermission,
  useHasExportBusinessesPermission,
  useHasSaleyardOrDeploymentPermission,
  useIsMobile,
} from "hooks";

import { columnsDefs } from "./BusinessListColumns";

const getRowId = params => params.data.business.id;

const additionalActions = [
  {
    default: true,
    title: "Add Business",
    isDisabled: false,
    onClick: () => openEditBusinessModal(),
    icon: faPlus,
    dataTour: "addBusiness",
  },
];
export function BusinessTable() {
  const rowData = useDebounceSelector(selectBusinessesAggridData);
  const propertyByIdLookup = useSelector(getProperties);
  const defaultSplitByBusinessIdLookup = useSelector(
    selectDefaultVendorSplitByBusinessIdLookup,
  );
  const hasExportCsvPermissions = useHasExportBusinessesPermission();
  const context = useMemo(
    () => ({
      defaultSplitByBusinessIdLookup,
      propertyByIdLookup,
    }),
    [defaultSplitByBusinessIdLookup, propertyByIdLookup],
  );

  const isMobile = useIsMobile();
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureInterest,
  );
  const enableInterest = useSelector(getDefaultBillingSettings)?.enableInterest;
  const isInterestEnabled = useMemo(
    () => hasInterestFeature && enableInterest,
    [hasInterestFeature, enableInterest],
  );

  const hasMyobExportsFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureMyobIntegration,
  );
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const onRowSelectionChange = useCallback(
    rows => {
      const newSelectedBusinesses = rows.map(r => r.business.id);
      if (
        newSelectedBusinesses.length !== selectedBusinesses.length ||
        !newSelectedBusinesses.every(id => selectedBusinesses.includes(id))
      ) {
        setSelectedBusinesses(newSelectedBusinesses);
      }
    },
    [selectedBusinesses],
  );

  const [
    isEditBusinessesModalOpen,
    openEditBusinessesModal,
    closeEditBusinessesModal,
  ] = useBoolean(false);

  const hasBulkUpdateBusinessPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureBulkUpdateBusinesses,
    SaleyardPermissions.featureBulkUpdateBusinesses,
  );
  const hasCreateGlobalBusinessFeature = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureGlobalBusinessSearch,
    SaleyardPermissions.featureGlobalBusinessSearch,
  );

  const trimmedActions = useMemo(() => {
    const actions = [...additionalActions];
    if (hasCreateGlobalBusinessFeature) {
      actions.push({
        title: "Add Connected Business",
        isDisabled: false,
        onClick: () => openCreateGlobalBusinessModal(),
        icon: faBook,
        dataTour: "addGlobalBusiness",
      });
    }
    if (hasBulkUpdateBusinessPermission) {
      actions.push({
        dataTour: "bulkUpdate",
        title: `Bulk Update ${selectedBusinesses.length} Businesses`,
        onClick: () => openEditBusinessesModal(),
        icon: faPencil,
        isDisabled: selectedBusinesses.length === 0,
      });
    }
    return actions;
  }, [
    hasCreateGlobalBusinessFeature,
    hasBulkUpdateBusinessPermission,
    selectedBusinesses.length,
    openEditBusinessesModal,
  ]);

  const memoizedColumnDefs = useMemo(
    () =>
      columnsDefs(isLivestockAgent, isInterestEnabled, hasMyobExportsFeature),
    [isLivestockAgent, isInterestEnabled, hasMyobExportsFeature],
  );

  return (
    <Column fullHeight>
      <AgGridTable
        additionalActions={trimmedActions}
        columnDefs={memoizedColumnDefs}
        context={context}
        getRowId={getRowId}
        rowData={rowData}
        tableName={AgGridTables.BUSINESSES}
        showTextFilter
        cacheQuickFilter
        hasExportCsvPermissions={hasExportCsvPermissions}
        suppressCellSelection={!!isMobile}
        hideSideBar={!!isMobile}
        hideSavedViews={!!isMobile}
        rowsSelectable
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection="multiple"
      />
      {isEditBusinessesModalOpen && (
        <BulkUpdateBusinessModal
          onClose={closeEditBusinessesModal}
          businessIds={selectedBusinesses}
        />
      )}
    </Column>
  );
}
